/**
 * useScreenTheme — Web
 *
 * Returns a merged screen-theme object:
 *   base  = screenThemes[screenName][mode]          (static colors for light/dark)
 *   brand = resolveClientOverrides(tokens, key, mode) (brand overrides, mode-aware)
 *   result = { ...base, ...brand[screenName] }
 *
 * Color mode resolution (web — no OS-level scheme API):
 *   Redux `colorMode === 'dark'`            → dark mode
 *   Redux `colorMode === 'light' | 'system'`→ light mode (web default)
 *
 * Dispatching:
 *   dispatch(setClientTheme('orange'))  — switch brand color
 *   dispatch(setColorMode('dark'))      — enable dark mode
 *   dispatch(setColorMode('light'))     — revert to light mode
 */
import { useSelector } from 'react-redux';
import { screenThemes } from '../theme/screenThemes';
import {
  getClientThemeTokens,
  resolveClientOverrides,
  applyClientOverride,
} from '../theme/clientThemes';
import type { RootState } from '../store';

export const useScreenTheme = <T extends keyof typeof screenThemes>(
  screenName: T,
): (typeof screenThemes)[T]['light' | 'dark'] => {
  // Read user's color-mode preference from Redux
  const colorMode = useSelector(
    (state: RootState) => state.theme?.colorMode ?? 'system',
  );

  // On web, 'system' defaults to light (no reliable OS dark-mode detection
  // without a window.matchMedia listener — keep simple for now).
  const mode: 'light' | 'dark' = colorMode === 'dark' ? 'dark' : 'light';

  // Read active brand theme name from Redux (defaults to 'blue')
  const themeName = useSelector(
    (state: RootState) => state.theme?.themeName ?? 'blue',
  );

  // 1. Static base colors for this screen + mode
  const base = screenThemes[screenName][mode];

  // 2. Brand-specific overrides, mode-aware (cached per theme+mode)
  const tokens = getClientThemeTokens(themeName);
  const overrides = resolveClientOverrides(tokens, `${themeName}:${mode}`, mode);

  // 3. Shallow-merge: only brand-colored keys are replaced
  return applyClientOverride(
    base as Record<string, string>,
    overrides[screenName as string],
  ) as (typeof screenThemes)[T]['light' | 'dark'];
};